import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const ROLES = [
  { value: "composer", label: "Composer" },
  { value: "songwriter", label: "Songwriter" },
  { value: "both", label: "Both" },
];

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState({
    name: "", bio: "", country: "", role: "composer", genres: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        bio: user.bio || "",
        country: user.country || "",
        role: user.role || "composer",
        genres: (user.genres || []).join(", "),
      });
    }
  }, [user]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        bio: form.bio,
        country: form.country,
        role: form.role,
        genres: form.genres.split(",").map((g) => g.trim()).filter(Boolean),
      };
      await api.put("/profile", payload);
      await refreshUser();
      toast.success("Profile updated");
    } catch {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0C10] pt-28 pb-20">
      <div className="max-w-3xl mx-auto px-6">
        <div className="overline">Your identity</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-2 tracking-tight">Profile</h1>

        <form onSubmit={onSave} className="glass-card p-8 mt-10 space-y-5 fade-up">
          <div className="flex items-center gap-5">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#7DA3D9]/40 to-[#1E293B] border border-white/10 flex items-center justify-center text-3xl font-display text-[#B8D0EE]">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <div className="text-sm text-slate-400">{user?.email}</div>
              <div className="overline mt-1">Member since {user?.created_at ? new Date(user.created_at).getFullYear() : "—"}</div>
            </div>
          </div>

          <div className="divider-gold" />

          <div>
            <label className="overline block mb-2">Display name</label>
            <input className="input-dark" value={form.name} onChange={update("name")} data-testid="profile-name-input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-2">Role</label>
              <select className="input-dark" value={form.role} onChange={update("role")} data-testid="profile-role-select">
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value} className="bg-[#0A0C10]">{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Country</label>
              <input className="input-dark" value={form.country} onChange={update("country")} data-testid="profile-country-input" />
            </div>
          </div>

          <div>
            <label className="overline block mb-2">Bio</label>
            <textarea
              className="input-dark min-h-[120px] resize-none"
              value={form.bio}
              onChange={update("bio")}
              placeholder="Tell the society about your work…"
              data-testid="profile-bio-input"
            />
          </div>

          <div>
            <label className="overline block mb-2">Genres (comma-separated)</label>
            <input
              className="input-dark"
              value={form.genres}
              onChange={update("genres")}
              placeholder="Orchestral, Film Score, Indie"
              data-testid="profile-genres-input"
            />
          </div>

          <button type="submit" disabled={loading} className="btn-gold disabled:opacity-50" data-testid="profile-save-btn">
            {loading ? "Saving…" : "Save changes"}
          </button>
        </form>
      </div>
    </div>
  );
}
